import {
  AccountPaymentMethod,
  AuthorizationVerbiageType,
  BurnToken,
  BuyerAccountInformation,
  CreatePaymentAccountPayload,
  getFirstContact,
  mapOriginatingApplication,
  PaymentExecutionV1Request,
  RemittanceEmail,
  RetrieveAuthorizationVerbiageRequestV1,
  RetrieveSurchargeAuthorizationVerbiagePayload,
  UpdatePaymentTransactionV1Request,
  UpdatePaymentV1Request,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  CutOffTimeStore,
  PaymentRuleStore,
  selectAllowACH,
  selectBankHolidayDates,
  selectBuyerAccounts,
  selectBuyerCompanyName,
  selectCustomTemplatesSettings,
  selectIsBuyerOriginApp,
  selectIsPaymentLinks,
  selectOriginatingApplication,
  selectPaymentAccounts,
  selectPaymentRemittanceRules,
  selectRecipients,
  selectSessionBuyerAccountIdentifierWithoutPrefix,
  selectSessionId,
  selectVirtualOtherwisePrimaryTenantId,
  selectTenantName,
  selectUserEmail,
  SessionStore,
  selectPaymentAuthorizationChannel,
  selectCaptchaToken,
  selectIsCountryCanada,
  selectIsRemitUploadEnabled,
  selectIsUnauthenticatedPaymentLinksUser,
  selectShowSignInBanner as selectShowSignInBannerBase,
} from '@arc/unified-payments-frontend/shared-stores';
import {
  selectGetSessionFromPaymentLinkResponse,
  selectIsStaticPaymentLink,
} from '@arc/unified-payments-frontend/payment-links';
import { createSelector } from '@ngrx/store';
import {
  selectCvv,
  selectIsScheduledPaymentDate,
  selectPaymentDate,
  selectPaymentDetails,
  selectPaymentPreview,
  selectSelectedPaymentAccount,
} from '../reducers';
import { AuthUserProfileStore } from '@arc/arc-shared-frontend';
import { isDateInPast } from '@arc/unified-payments-frontend/date-utils';
import {
  selectCurrentLanguage,
  selectLanguageCode,
} from '@arc/unified-payments-frontend/i18n';

export const selectAccounts = createSelector(
  selectBuyerAccounts,
  selectSessionBuyerAccountIdentifierWithoutPrefix,
  selectIsBuyerOriginApp,
  selectIsPaymentLinks,
  selectIsStaticPaymentLink,
  selectPaymentDetails,
  (
    accounts,
    sessionAccountIdentifier,
    isBuyerOriginApp,
    isPaymentLinks,
    isStaticPaymentLink,
    paymentDetails,
  ): BuyerAccountInformation[] => {
    // Static payment links: user enters account identifier manually.
    // Use the account stored in paymentDetails after form submission.
    // Fall back to [{ accountNumber: null }] while still on payment details step
    // (preserves existing behaviour for selectDefaultPaymentDetails / component init).
    if (isStaticPaymentLink) {
      return paymentDetails?.buyerAccount != null
        ? [paymentDetails.buyerAccount]
        : [{ accountNumber: null }];
    }

    // For payment links: always use session buyer account identifier
    if (isPaymentLinks) {
      return [{ accountNumber: sessionAccountIdentifier }];
    }

    // For buyer origin apps: return buyer accounts list (from API)
    // For supplier origin apps: return session buyer account
    return isBuyerOriginApp
      ? accounts
      : [{ accountNumber: sessionAccountIdentifier }];
  },
);

export const selectAccountNumbers = createSelector(
  selectAccounts,
  (accounts) => {
    return accounts?.map((x) => x.accountNumber);
  },
);

export const selectInitialDataLoaded = createSelector(
  PaymentRuleStore,
  selectPaymentPreview,
  selectAccounts,
  selectIsStaticPaymentLink,
  (rules, preview, accounts, isStaticPaymentLink) => {
    const previewReady = isStaticPaymentLink || preview != null;
    return rules != null && previewReady && accounts != null;
  },
);

export const selectCustomTemplateData = createSelector(
  selectGetSessionFromPaymentLinkResponse,
  SessionStore,
  selectPaymentDetails,
  (paymentLinkResponse, sessionSummary, paymentDetails) => {
    return (
      paymentLinkResponse?.customTemplateData ||
      sessionSummary?.customTemplateData ||
      paymentDetails?.customTemplateData ||
      null
    );
  },
);

export const selectUserRestoredTemplateFields = createSelector(
  selectPaymentDetails,
  (paymentDetails) => paymentDetails?.customTemplateData?.templateFields ?? [],
);

export const selectDefaultPaymentDetails = createSelector(
  selectPaymentDetails,
  selectRecipients,
  selectAccounts,
  selectCustomTemplateData,
  (paymentDetails, recipients, accounts, customTemplateData) => {
    // Create a new object to avoid mutating the original
    const updatedDetails = { ...paymentDetails };

    if (updatedDetails.buyerAccount == null) {
      updatedDetails.buyerAccount = accounts[0];
    }

    if (updatedDetails.receiptRecipients == null) {
      updatedDetails.receiptRecipients = recipients;
    }

    // Override amount with customTemplateData.amount if available
    if (customTemplateData?.amount != null) {
      updatedDetails.amount = customTemplateData.amount;
    }

    return updatedDetails;
  },
);

export const selectScheduledPaymentDate = createSelector(
  selectPaymentPreview,
  (preview) => {
    if (preview == null || !preview.payments) {
      return null;
    }
    return preview.payments[0].paymentSchedule.paymentDate;
  },
);

export const selectPaymentReference = createSelector(
  selectPaymentPreview,
  (preview) => {
    if (preview == null || !preview.payments) {
      return null;
    }
    return preview.payments[0].paymentReference.burn;
  },
);

export const selectPaymentReferenceNumber = createSelector(
  selectPaymentPreview,
  (preview) => {
    if (preview == null || !preview.payments) {
      return null;
    }
    const burn = BurnToken.parse(preview.payments[0].paymentReference.burn);
    return burn.propertyValue;
  },
);

export const selectTransactionReferenceNumber = createSelector(
  selectPaymentPreview,
  (preview) => {
    if (preview == null || !preview.payments) {
      return null;
    }
    const burn = BurnToken.parse(
      preview.payments[0].payment.transactionReference.burn,
    );
    return burn.propertyValue;
  },
);

export const selectTotalSurcharge = createSelector(
  selectPaymentPreview,
  (preview) => {
    if (preview == null || !preview.payments) {
      return null;
    }
    return preview.payments?.[0]?.payment.totalSurcharge ?? null;
  },
);

export const selectIsSurchargeFeeVisible = createSelector(
  selectTotalSurcharge,
  (surchargeSubTotal) => {
    return surchargeSubTotal > 0;
  },
);

export const selectAmount = createSelector(
  selectPaymentDetails,
  (paymentDetails) => {
    return paymentDetails.amount;
  },
);

export const selectOriginalPaymentDate = createSelector(
  selectPaymentDetails,
  (paymentDetails) => {
    return paymentDetails.paymentDate;
  },
);

export const selectSingleBuyerAccountNumber = createSelector(
  selectPaymentDetails,
  (paymentDetails) => {
    return paymentDetails.buyerAccount.accountNumber;
  },
);
export const selectNameOnBankAccount = createSelector(
  selectSelectedPaymentAccount,
  selectPaymentAccounts,
  (selectedPaymentAccount, paymentAccounts) => {
    return paymentAccounts?.find(
      (x) =>
        x.paymentAccountIdentifierBurn ===
        selectedPaymentAccount.paymentAccountIdentifierBurn,
    )?.nameOnBankAccount;
  },
);

export const selectPaymentMethodType = createSelector(
  selectSelectedPaymentAccount,
  (paymentAccount) => {
    return paymentAccount?.paymentMethod;
  },
);

export const selectBillingAddressCountry = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) => {
    return selectedPaymentAccount?.country;
  },
);

export const selectPaymentAccountDisplayName = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) => {
    return selectedPaymentAccount.displayName;
  },
);

export const selectSelectedPaymentMethodType = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) => {
    return selectedPaymentAccount.paymentMethod;
  },
);

export const selectRecalculateSurcharge = createSelector(
  selectSelectedPaymentMethodType,
  (paymentMethodType) => {
    return (
      paymentMethodType == AccountPaymentMethod.Card ||
      paymentMethodType == AccountPaymentMethod.Ach
    );
  },
);

export const selectIsScheduledPaymentDateChangedPayload = createSelector(
  selectOriginalPaymentDate,
  selectBankHolidayDates,
  CutOffTimeStore,
  (originalPaymentDate, bankHolidays, cutOffTime) => {
    return {
      paymentDate: originalPaymentDate,
      bankHolidays,
      cutOffTime,
    };
  },
);

export const selectPrefixedBuyerAccountIdentifier = createSelector(
  selectPaymentDetails,
  (paymentDetails) => {
    return 'accountNumber:' + paymentDetails.buyerAccount.accountNumber;
  },
);

export const selectEmailForPaymentAccount = createSelector(
  selectIsBuyerOriginApp,
  selectPaymentDetails,
  (isBuyerOriginApp, paymentDetails) => {
    // Get the first contact
    const firstContact = getFirstContact(paymentDetails.receiptRecipients);

    // Return null if there's no first contact or if it's a buyer workflow
    return !isBuyerOriginApp && firstContact ? firstContact : null;
  },
);

export const selectCreatePaymentAccountPayload = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  SessionStore,
  selectPrefixedBuyerAccountIdentifier,
  selectEmailForPaymentAccount,
  (tenantId, sessionSummary, prefixedBuyerAccountIdentifier, emailAddress) => {
    const payload: CreatePaymentAccountPayload = {
      tenantId: tenantId,
      originatingApplication: sessionSummary?.originatingApplication,
      originSystem: mapOriginatingApplication(
        sessionSummary?.originatingApplication,
      ),
      buyerAccountIdentifiers: [prefixedBuyerAccountIdentifier],
      externalUserUrn: sessionSummary?.externalUserUrn,
      emailAddress: emailAddress,
    };
    return payload;
  },
);

export const selectIsCardPaymentMethod = createSelector(
  selectPaymentMethodType,
  (paymentMethod) => {
    return paymentMethod === AccountPaymentMethod.Card;
  },
);

export const selectIsACHPaymentMethod = createSelector(
  selectPaymentMethodType,
  (paymentMethod) => {
    return paymentMethod === AccountPaymentMethod.Ach;
  },
);

export const selectIsBankPaymentAndCountryNotCanada = createSelector(
  selectIsACHPaymentMethod,
  selectIsCountryCanada,
  (isAchPaymentMethod, isCountryCanada) =>
    isAchPaymentMethod && !isCountryCanada,
);

export const selectExecutePaymentRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectPaymentReferenceNumber,
  selectTransactionReferenceNumber,
  selectIsScheduledPaymentDate,
  selectCvv,
  selectIsACHPaymentMethod,
  selectPaymentAuthorizationChannel,
  selectCaptchaToken,
  (
    tenantId,
    paymentReferenceNumber,
    transactionReferenceNumber,
    isScheduledPayment,
    cvv,
    isAchPayment,
    paymentAuthorizationChannel,
    captchaToken,
  ) => {
    const request: PaymentExecutionV1Request = {
      cvv,
      paymentAuthorizationChannel,
      ...(captchaToken != null && { captchaToken }),
    };
    return {
      tenantId,
      paymentReferenceNumber,
      transactionReferenceNumber,
      isScheduledPayment: isAchPayment || isScheduledPayment,
      request,
    };
  },
);

export const selectUpdateDomesticPayOnAccountSessionPaymentRequest =
  createSelector(
    selectPaymentPreview,
    selectPaymentReferenceNumber,
    selectSelectedPaymentAccount,
    selectPaymentDetails,
    (paymentPreview, paymentReference, paymentAccount, details) => {
      const transactions = paymentPreview.payments.map((payment) => {
        const paymentTransaction = payment.payment;
        const transaction: UpdatePaymentTransactionV1Request = {
          buyerAccountIdentifier: payment.account.buyerAccountIdentifier,
          currencyCode: paymentTransaction.paymentCurrency,
          payables: [],
          paymentAccount: {
            burn: paymentAccount.paymentAccountIdentifierBurn,
            displayName: paymentAccount.displayName,
            paymentMethod: paymentAccount.paymentMethod,
            nameOnAccount:
              paymentAccount.paymentMethod === AccountPaymentMethod.Ach
                ? paymentAccount.nameOnBankAccount
                : paymentAccount.nameOnCard,
          },
          totalAdditionalFee: paymentTransaction.totalAdditionalFee,
          totalAmount: paymentTransaction.totalAmount,
          totalDiscount: paymentTransaction.totalDiscount,
          totalServiceFee: paymentTransaction.totalServiceFee,
          totalSurcharge: paymentTransaction.totalSurcharge,
          transactionReference: BurnToken.parse(
            paymentTransaction.transactionReference.burn,
          ).propertyValue,
        };

        return transaction;
      });
      const updatePaymentRequest: UpdatePaymentV1Request = {
        paymentProcess: 'Internal',
        paymentReference,
        receipts: details.receiptRecipients,
        transactions,
        paymentNotes: details.paymentNotes || '-',
        referenceNumber: details.referenceNumber || null,
        referenceDate: details.referenceDate || null,
      };
      return updatePaymentRequest;
    },
  );

export const selectUpdateDomesticPaymentRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectSessionId,
  selectUpdateDomesticPayOnAccountSessionPaymentRequest,
  (tenantId, sessionId, updatePaymentRequest) => ({
    tenantId,
    sessionId,
    request: updatePaymentRequest,
  }),
);

export const selectRemittanceFile = createSelector(
  selectPaymentDetails,
  (paymentDetails) => {
    return paymentDetails.remittanceFile;
  },
);

export const selectHasRemittanceFileToUpload = createSelector(
  selectRemittanceFile,
  selectIsRemitUploadEnabled,
  (remittanceFile, isRemitUploadEnabled) => {
    return isRemitUploadEnabled && !!remittanceFile;
  },
);

export const selectRemittanceEmailRequest = createSelector(
  selectPaymentRemittanceRules,
  selectSelectedPaymentMethodType,
  selectPaymentDate,
  selectPaymentDetails,
  selectBuyerCompanyName,
  selectTransactionReferenceNumber,
  selectIsBuyerOriginApp,
  selectOriginatingApplication,
  selectUserEmail,
  (
    remittanceRules,
    paymentMethodType,
    paymentDate,
    paymentDetails,
    buyerCompanyName,
    transactionReferenceNumber,
    isBuyerOriginApp,
    originatingApplication,
    userEmail,
  ): RemittanceEmail => {
    return {
      sendToEmails:
        paymentMethodType == AccountPaymentMethod.Ach
          ? remittanceRules?.achEmails
          : remittanceRules?.creditCardEmails,
      buyerCompanyName,
      paymentDate,
      amount: paymentDetails.amount,
      buyerAccountNumber: paymentDetails.buyerAccount.accountNumber,
      paymentNotes: paymentDetails.paymentNotes,
      referenceNumber: paymentDetails.referenceNumber,
      referenceDate: paymentDetails.referenceDate,
      transactionReferenceNumber,
      paymentMethod: paymentMethodType,
      isBuyerOriginApp: isBuyerOriginApp,
      originatingApplication: originatingApplication,
      userEmail: userEmail,
      receiptEmails: paymentDetails.receiptRecipients.join(','),
    };
  },
);

export const selectPaymentMethodDisplayName = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) => selectedPaymentAccount?.displayName,
);

export const selectIsCardExpired = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) => {
    return isDateInPast(
      selectedPaymentAccount?.expirationMonth,
      selectedPaymentAccount?.expirationYear,
    );
  },
);

export const selectShowUpdateExpirationDateInput = createSelector(
  selectIsCardPaymentMethod,
  selectIsCardExpired,
  (isCardPaymentMethod, isCardExpired) => {
    return isCardPaymentMethod && isCardExpired;
  },
);

export const selectPoaNameOnAccount = createSelector(
  selectIsBuyerOriginApp,
  selectDefaultPaymentDetails,
  AuthUserProfileStore,
  (isBuyerOriginApp, paymentDetails, user) => {
    // Buyer Flow
    if (isBuyerOriginApp) {
      return `${user.firstName} ${user.lastName}`;
    }

    // Supplier Flow
    return paymentDetails.buyerAccount.accountNumber;
  },
);

export const selectGetPoaSurchargeAuthorizationVerbiageRequest = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectTenantName,
  selectOriginatingApplication,
  selectPoaNameOnAccount,
  selectLanguageCode,
  selectAllowACH,
  selectIsUnauthenticatedPaymentLinksUser,
  (
    tenantId,
    tenantName,
    originApplication,
    nameOnAccount,
    language,
    allowACH,
    isUnauthenticatedPaymentLinksUser,
  ) => {
    const payload: RetrieveSurchargeAuthorizationVerbiagePayload = {
      allowACH,
      tenantId: tenantId,
      request: {
        originApplication,
        variableData: [
          {
            key: 'NameOnAccount',
            value: nameOnAccount,
          },
          {
            key: 'CompanyName',
            value: tenantName,
          },
        ],
        language,
        verbiageType: isUnauthenticatedPaymentLinksUser
          ? AuthorizationVerbiageType.PaymentLinksSurchargeAuthorization
          : AuthorizationVerbiageType.SurchargeAuthorization,
      },
    };
    return payload;
  },
);

export const selectRetrieveBankAuthVerbiagePoaRequestData = createSelector(
  selectVirtualOtherwisePrimaryTenantId,
  selectTenantName,
  selectCurrentLanguage,
  selectOriginatingApplication,
  selectPoaNameOnAccount,
  selectIsUnauthenticatedPaymentLinksUser,
  (
    tenantId,
    tenantName,
    currentLanguage,
    originatingApplication,
    nameOnAccount,
    isUnauthenticatedPaymentLinksUser,
  ) => {
    const authVerbiageRequest: RetrieveAuthorizationVerbiageRequestV1 = {
      language: currentLanguage,
      originApplication: originatingApplication,
      variableData: [
        {
          key: 'NameOnAccount',
          value: nameOnAccount,
        },
        {
          key: 'CompanyName',
          value: tenantName,
        },
      ],
      verbiageType: isUnauthenticatedPaymentLinksUser
        ? AuthorizationVerbiageType.PaymentLinksBankAuthorization
        : AuthorizationVerbiageType.BankAuthorization,
    };

    return {
      tenantId: tenantId,
      request: authVerbiageRequest,
    };
  },
);

export const selectPaymentAccountBurn = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) =>
    selectedPaymentAccount?.paymentAccountIdentifierBurn,
);

export const selectPadSignedSeparatelyForPaymentAccount = createSelector(
  selectSelectedPaymentAccount,
  (selectedPaymentAccount) =>
    selectedPaymentAccount?.paymentMethodFormDetails?.padSignedSeparately,
);

/* istanbul ignore next */
export const selectMatchedTemplateSettings = createSelector(
  selectCustomTemplateData,
  selectCustomTemplatesSettings,
  (customTemplateData, customTemplatesSettings) => {
    if (!customTemplateData?.templateId || !customTemplatesSettings?.length) {
      return null;
    }
    return (
      customTemplatesSettings.find(
        (template) => template.templateId === customTemplateData.templateId,
      ) || null
    );
  },
);

export const selectIsCustomTemplatesEnabled = createSelector(
  selectMatchedTemplateSettings,
  (matchedSettings) => matchedSettings !== null,
);

export const selectIsPaymentLinksCaptchaRequired = createSelector(
  selectMatchedTemplateSettings,
  (matchedSettings) => matchedSettings?.captchaEnabled ?? false,
);

export const selectIsValidatingTemplate = createSelector(
  selectMatchedTemplateSettings,
  (matchedSettings) => matchedSettings?.templateType === 'Validating',
);

export const selectIsNonValidatingTemplate = createSelector(
  selectMatchedTemplateSettings,
  (matchedSettings) => matchedSettings?.templateType === 'NonValidating',
);

export const selectShowSignInBanner = createSelector(
  selectShowSignInBannerBase,
  selectIsNonValidatingTemplate,
  (showBanner, isNonValidatingTemplate) =>
    showBanner && !isNonValidatingTemplate,
);
